// Coordinate same-origin ODP tabs without localStorage or persisting tokens.
const CHANNEL = "odp-auth-logout-v1";
const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL) : null;
const listeners = new Set<() => void>();
const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
let loggingOut = false;
let logoutId: string | null = null;
const acknowledgements = new Set<string>();
const debug = (event: string, details: Record<string, unknown> = {}): void => {
  console.warn("[ODP-CROSS-TAB-DEBUG]", event, {
    origin: window.location.origin,
    path: window.location.pathname,
    tabId,
    channelAvailable: !!channel,
    loggingOut,
    listenerCount: listeners.size,
    ...details,
  });
};
debug("CHANNEL_INITIALIZED");

export const isCrossTabLogoutInProgress = (): boolean => loggingOut;

export const broadcastCrossTabLogout = (): void => {
  debug("BROADCAST_REQUESTED");
  if (loggingOut) {
    debug("BROADCAST_SKIPPED_ALREADY_LOGGING_OUT");
    return;
  }
  loggingOut = true;
  logoutId = `${tabId}-${Date.now()}`;
  channel?.postMessage({ type: "logout", source: tabId, logoutId });
  debug("BROADCAST_POSTED", { logoutId });
};

// Allow peers time to acknowledge before SiteMinder navigates the sending tab away.
// The bounded wait does not block logout if a peer is unavailable.
export const waitForCrossTabLogoutAcknowledgement = async (timeoutMs = 800): Promise<void> => {
  if (!channel || !logoutId) return;
  const requestedId = logoutId;
  const start = Date.now();
  while (Date.now() - start < timeoutMs && !acknowledgements.has(requestedId)) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 50));
  }
  debug(acknowledgements.has(requestedId) ? "LOGOUT_ACK_CONFIRMED" : "LOGOUT_ACK_TIMEOUT", {
    logoutId: requestedId,
    waitMs: Date.now() - start,
  });
};

export const subscribeCrossTabLogout = (listener: () => void): (() => void) => {
  listeners.add(listener);
  debug("LISTENER_ADDED");
  // Automatic channel diagnostic: harmless ping, never logs out a tab.
  channel?.postMessage({ type: "diagnostic-ping", source: tabId });
  debug("DIAGNOSTIC_PING_POSTED");
  return () => { listeners.delete(listener); debug("LISTENER_REMOVED"); };
};

if (channel) {
  channel.onmessage = (event: MessageEvent) => {
    const data = event.data;
    debug("MESSAGE_RECEIVED", { messageType: data?.type, source: data?.source });
    if (!data || data.source === tabId) return;
    if (data.type === "diagnostic-ping") {
      channel.postMessage({ type: "diagnostic-pong", source: tabId, target: data.source });
      debug("DIAGNOSTIC_PING_RECEIVED_PONG_POSTED", { target: data.source });
      return;
    }
    if (data.type === "diagnostic-pong") {
      if (data.target === tabId) debug("DIAGNOSTIC_PONG_RECEIVED", { source: data.source });
      return;
    }
    if (data.type === "logout-ack") {
      if (data.target === tabId && typeof data.logoutId === "string") {
        acknowledgements.add(data.logoutId);
        debug("LOGOUT_ACK_RECEIVED", { source: data.source, logoutId: data.logoutId });
      }
      return;
    }
    if (data.type !== "logout" || loggingOut) {
      debug("MESSAGE_IGNORED", { messageType: data.type });
      return;
    }
    loggingOut = true;
    if (typeof data.logoutId === "string") {
      channel.postMessage({ type: "logout-ack", source: tabId, target: data.source, logoutId: data.logoutId });
      debug("LOGOUT_ACK_POSTED", { logoutId: data.logoutId });
    }
    debug("LOGOUT_DISPATCHING_TO_LISTENERS");
    listeners.forEach((listener) => listener());
  };
}